/**
 * Layout component that queries for data
 * with Gatsby's useStaticQuery component
 *
 * See: https://www.gatsbyjs.com/docs/how-to/querying-data/use-static-query/
 */

import React, { useEffect, ReactNode, useState } from "react";
import { useStaticQuery, graphql } from "gatsby";

import Header from "./Header";
import Footer from "./Footer";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store";
import { fetchAllCurrencyCodes } from "../../../store/currencyCode";
import CookieBanner from "../../seo/CookieBanner";
import { useBreadcrumb } from "gatsby-plugin-breadcrumb";
import CustomBreadcrumb from "../../shared/CustomBreadcrumb";
import SalesChannelContext from "../../../context/SalesChannelContext";
import { Helmet } from "react-helmet";
import { getSalesChannelImage } from "../../../utils/image";

interface Props {
    children: ReactNode;
    pageTemplate?: any;
}

const Layout = ({ children, pageTemplate }: Props) => {
    const dispatch = useDispatch<AppDispatch>();

    const currencyCode = useSelector(
        (state: RootState) => state.currencyCode?.selectedCurrencyCode?.currencyCode
    );

    useEffect(() => {
        if (!currencyCode) {
            dispatch(fetchAllCurrencyCodes());
        }
    }, []);

    const data = useStaticQuery(graphql`
        query {
            salesChannel {
                id
                name
                websiteTemplate
                email
                logoFile {
                    childImageSharp {
                        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
                    }
                    publicURL
                }
                iconFile {
                    childImageSharp {
                        gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
                    }
                    publicURL
                }
                phone
                address
                metadata
                type
                noIndex
                taxes {
                    id
                    name
                    taxRates {
                        id
                        name
                        taxRate
                        countryCodeRegex
                        zipCodeRegex
                    }
                }
                donations {
                    id
                    name
                    desc
                    type
                    value
                    image
                    enabled
                    salesChannel
                }
                aggregateReview {
                    count
                    ratingValue
                }
            }
            allMenus: allMenu {
                nodes{
                    id
                    name
                    displayName
                    items{
                        id
                        name
                        target
                        menu
                        sort
                        link {
                            pageId
                            src
                            target
                            text
                        }
                        childMenus {
                            id
                            name
                        }
                    }
                }
            }
            menuItems: allMenuItem {
                nodes {
                    id
                    name
                    target
                    menu
                    sort
                    link {
                        pageId
                        src
                        target
                        text
                    }
                }
            }
            allTemplates: allPageTemplate(limit: 100) {
                nodes {
                    id
                    name
                    headerLogoFile {
                        childImageSharp {
                            gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
                        }
                    }
                    footerLogoFile {
                        childImageSharp {
                            gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP, AVIF])
                        }
                    }
                    header {
                        enabled
                        menu
                        menus {
                            id
                            name
                            displayName
                        }
                        template
                        logo
                    }
                    footer {
                        enabled
                        menu
                        template
                        logo
                        menus {
                            id
                            name
                            displayName
                        }
                    }
                }
            }
            socials: allSocial(filter: { displayOnSite: { eq: true } }) {
                nodes {
                    id
                    type
                    linkUrl
                    displayOnSite
                }
            }
        }
    `);

    const { salesChannel, allTemplates, menuItems, socials, allMenus } = data;

    if (!pageTemplate) {
        pageTemplate = allTemplates.nodes[0];
    } else {
        pageTemplate = allTemplates.nodes.find((template: any) => template.id === pageTemplate.id);
    }

    return (
        <>
            <SalesChannelContext.Provider value={{ salesChannel }}>
                <Helmet>
                    <meta charSet="utf-8" />
                    {salesChannel?.iconFile?.publicURL && (
                        <link rel="icon" href={salesChannel?.iconFile.publicURL as string} />
                    )}
                </Helmet>

                <Header
                    pageTemplate={pageTemplate}
                    salesChannel={salesChannel}
                    allMenus={allMenus?.nodes || []}
                />
                <main className="text-secondary">{children}</main>
                <Footer
                    pageTemplate={pageTemplate}
                    salesChannel={salesChannel}
                    menuItems={menuItems}
                    socials={socials?.nodes}
                />
                <CookieBanner />
            </SalesChannelContext.Provider>
        </>
    );
};

export default Layout;
